/**
 * beautifi - Main Entry Point
 *
 * This is the public API for the beautifi plugin.
 * It coordinates the ImageDetector, ViewportObserver, and animation system.
 */

import {
    LivePhotoOptions,
    TrackedImage,
    DEFAULT_OPTIONS,
    LivePhotoError,
} from './types';
import { ImageDetector } from './ImageDetector';
import { ViewportObserver } from './ViewportObserver';
import { GeminiApiClient } from './GeminiApiClient';
import { AnimationRenderer } from './AnimationRenderer';
import { VeoClient } from './VeoClient';
import { VideoRenderer } from './VideoRenderer';
import { VideoLoadingQueue, QueuedVideo } from './VideoLoadingQueue';
import { SettingsClient } from './SettingsClient';

/**
 * beautifiPlugin class
 *
 * Core plugin that orchestrates image detection, viewport observation,
 * and animation coordination.
 */
class beautifiPlugin {
    private initialized: boolean = false;
    private options: Required<LivePhotoOptions> | null = null;
    private detector: ImageDetector | null = null;
    private viewportObserver: ViewportObserver | null = null;
    private preloadObserver: ViewportObserver | null = null;
    private apiClient: GeminiApiClient | null = null;
    private renderer: AnimationRenderer | null = null;
    private veoClient: VeoClient | null = null;
    private videoRenderer: VideoRenderer | null = null;
    private videoQueue: VideoLoadingQueue | null = null;
    private settingsClient: SettingsClient | null = null;
    private reducedMotion: boolean = false;
    private pendingVideoGenerations: Map<string, string> = new Map(); // imageId -> operationId
    private scrollHandler: (() => void) | null = null;
    private trackedImagesMap: Map<string, TrackedImage> = new Map(); // for queue callback lookup

    /**
     * Initialize the plugin with configuration options
     *
     * @param options - Plugin configuration
     * @throws Error if apiKey is not provided
     */
    async init(options: LivePhotoOptions): Promise<void> {
        if (!options.apiKey) {
            throw new Error('beautifi: apiKey is required');
        }

        // Check for reduced motion preference
        if (typeof window !== 'undefined' && window.matchMedia) {
            this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }

        // Merge with defaults
        this.options = {
            ...DEFAULT_OPTIONS,
            ...options,
        } as Required<LivePhotoOptions>;

        // If reduced motion is preferred and we respect it, don't animate
        if (this.reducedMotion && this.options.respectReducedMotion) {
            if (this.options.debug) {
                console.log('[beautifi] Reduced motion preference detected, animations disabled');
            }
            this.initialized = true;
            return;
        }

        // Create image detector
        this.detector = new ImageDetector(this.options.selector, this.options.debug, this.options.optIn);

        // Create API client
        this.apiClient = new GeminiApiClient({
            endpoint: this.options.endpoint || 'https://api.beautifi.uk/animate',
            timeout: this.options.timeout,
            maxRetries: this.options.maxRetries,
            enableCache: true,
            debug: this.options.debug,
        });

        // Create animation renderer
        this.renderer = new AnimationRenderer({
            debug: this.options.debug,
        });

        // Create settings client for portal integration
        this.settingsClient = new SettingsClient({
            apiKey: this.options.apiKey,
            endpoint: this.options.settingsEndpoint,
            cacheTTL: this.options.settingsCacheTTL,
            timeout: this.options.timeout,
            debug: this.options.debug,
        });

        // Create Veo client and video renderer for automatic video mode
        if (this.options.mode !== 'css') {
            this.veoClient = new VeoClient({
                endpoint: this.options.videoEndpoint || this.options.endpoint,
                apiKey: this.options.apiKey,
                debug: this.options.debug,
            });

            this.videoRenderer = new VideoRenderer({
                debug: this.options.debug,
                transitionDuration: 500,
            });

            if (this.options.debug) {
                console.log('[beautifi] Video mode enabled:', this.options.mode);
            }
        }

        // Create viewport observer (for CSS animations and visible state)
        this.viewportObserver = new ViewportObserver(
            (image, isIntersecting) => {
                this.handleVisibilityChange(image, isIntersecting);
            },
            {
                threshold: this.options.threshold,
                rootMargin: this.options.rootMargin,
                debug: this.options.debug,
            }
        );

        // Create video loading queue and preload observer (for video mode)
        if (this.options.mode !== 'css') {
            this.videoQueue = new VideoLoadingQueue({
                maxConcurrent: this.options.maxConcurrentVideos,
                onStartLoad: (video) => this.handleVideoQueueLoad(video),
                onUnload: (imageId) => this.handleVideoUnload(imageId),
                debug: this.options.debug,
            });

            // Preload observer uses unloadMargin for wider detection zone
            this.preloadObserver = new ViewportObserver(
                (image, isIntersecting) => {
                    this.handlePreloadZoneChange(image, isIntersecting);
                },
                {
                    threshold: 0,
                    rootMargin: this.options.unloadMargin,
                    debug: this.options.debug,
                }
            );
            this.preloadObserver.init();

            // Set up scroll handler to update priorities (debounced)
            let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
            this.scrollHandler = () => {
                if (scrollTimeout) return;
                scrollTimeout = setTimeout(() => {
                    scrollTimeout = null;
                    this.videoQueue?.updateAllPriorities(ViewportObserver.calculatePriority);
                }, 100);
            };
            window.addEventListener('scroll', this.scrollHandler, { passive: true });
        }

        // Set up image detection callback
        this.detector.setOnImageDetected(async (image) => {
            // Fetch portal settings for dynamically added images
            if (this.settingsClient) {
                try {
                    const portalSettings = await this.settingsClient.fetchSettings([image.imageUrl]);
                    const mergedConfig = this.settingsClient.mergeSettings(
                        image.imageUrl,
                        portalSettings,
                        image.config
                    );

                    // Update the tracked image's config
                    image.config = mergedConfig;
                    this.detector!.updateImageState(image.id, { config: mergedConfig });

                    if (this.options?.debug && portalSettings) {
                        console.log('[beautifi] Merged settings for dynamic image:', image.imageUrl, mergedConfig);
                    }
                } catch (error) {
                    // Continue with data attribute settings
                    if (this.options?.debug) {
                        console.warn('[beautifi] Failed to fetch settings for dynamic image:', error);
                    }
                }
            }

            this.viewportObserver!.observe(image);
            this.preloadObserver?.observe(image);
            this.trackedImagesMap.set(image.id, image);
        });

        // Initialize viewport observer
        this.viewportObserver.init();

        // Scan for existing images
        const images = this.detector.scan();

        // Fetch portal settings and merge with data attributes
        if (this.settingsClient && images.length > 0) {
            try {
                const imageUrls = images.map(img => img.imageUrl);
                const portalSettings = await this.settingsClient.fetchSettings(imageUrls);

                // Merge portal settings with data attributes for each image
                images.forEach((image) => {
                    const mergedConfig = this.settingsClient!.mergeSettings(
                        image.imageUrl,
                        portalSettings,
                        image.config
                    );

                    // Update the tracked image's config with merged settings
                    image.config = mergedConfig;
                    this.detector!.updateImageState(image.id, { config: mergedConfig });

                    if (this.options?.debug && portalSettings) {
                        console.log('[beautifi] Merged settings for:', image.imageUrl, mergedConfig);
                    }
                });
            } catch (error) {
                // Settings API unavailable — default to detect-only (no animation)
                // Images will be indexed but not animated until portal approves them
                if (this.options?.debug) {
                    console.warn('[beautifi] Settings API unavailable, images indexed but not animated:', error);
                }
            }
        }

        images.forEach((image) => {
            this.viewportObserver!.observe(image);
            this.preloadObserver?.observe(image);
            this.trackedImagesMap.set(image.id, image);
        });

        // Start observing DOM mutations
        this.detector.observe();

        this.initialized = true;

        if (this.options.debug) {
            console.log('[beautifi] Initialized with options:', this.options);
            console.log('[beautifi] Detected', images.length, 'images');
            console.log('[beautifi] Max concurrent videos:', this.options.maxConcurrentVideos);
        }
    }

    /**
     * Handle image entering/leaving preload zone (for video queue)
     */
    private handlePreloadZoneChange(image: TrackedImage, isIntersecting: boolean): void {
        if (!this.videoQueue) return;

        if (isIntersecting) {
            // Gate: don't preload videos for unapproved images
            if (!image.config.enabled) return;

            // Image entered preload zone - add to queue
            const priority = ViewportObserver.calculatePriority(image.element);
            this.videoQueue.enqueue(image.id, image.element, image.imageUrl, priority);
        } else {
            // Image left preload zone - remove from queue (triggers unload)
            this.videoQueue.remove(image.id);
        }
    }

    /**
     * Handle video queue load request
     */
    private async handleVideoQueueLoad(video: QueuedVideo): Promise<void> {
        const image = this.trackedImagesMap.get(video.imageId);
        if (!image) {
            this.videoQueue?.markError(video.imageId);
            return;
        }

        try {
            await this.startVideoGeneration(image);
            this.videoQueue?.markReady(video.imageId);
        } catch {
            this.videoQueue?.markError(video.imageId);
        }
    }

    /**
     * Handle video unload request
     */
    private handleVideoUnload(imageId: string): void {
        if (this.videoRenderer?.isPrepared(imageId)) {
            this.videoRenderer.stop(imageId);
            if (this.options?.debug) {
                console.log('[beautifi] Unloaded video:', imageId);
            }
        }
        // Cancel any pending operations
        this.pendingVideoGenerations.delete(imageId);
    }

    /**
     * Handle image entering/leaving viewport
     */
    private handleVisibilityChange(image: TrackedImage, isIntersecting: boolean): void {
        if (!this.options) return;

        if (isIntersecting) {
            // Image entered viewport - queue for animation
            if (image.state === 'idle') {
                this.queueAnimation(image);
            } else if (image.state === 'paused') {
                this.resumeAnimation(image);
            }
        } else {
            // Image left viewport - pause animation
            if (image.state === 'playing') {
                this.pauseAnimation(image);
            }
        }
    }

    /**
     * Queue an image for animation
     */
    private async queueAnimation(image: TrackedImage): Promise<void> {
        // Gate: only animate images approved in the portal
        if (!image.config.enabled) {
            if (this.options?.debug) {
                console.log('[beautifi] Skipping unapproved image:', image.imageUrl);
            }
            return;
        }

        // Update state to loading
        if (this.detector) {
            this.detector.updateImageState(image.id, { state: 'loading' });
        }

        if (this.options?.debug) {
            console.log('[beautifi] Queuing animation for:', image.imageUrl);
        }

        // Apply delay if configured
        if (image.config.delay && image.config.delay > 0) {
            await new Promise((resolve) => setTimeout(resolve, image.config.delay));
        }

        // Mode comes from merged portal settings first, then falls back to plugin defaults.
        const imageMode = image.config.mode ?? this.options?.mode ?? 'auto';

        try {
            // Fetch CSS animation data from API
            const animationData = await this.apiClient!.fetch(image.imageUrl, {
                type: image.config.type,
                intensity: image.config.intensity,
                loop: image.config.loop,
            });

            if (!animationData.success) {
                throw new LivePhotoError(
                    animationData.error || 'Animation generation failed',
                    'API_ERROR'
                );
            }

            // Store animation data on tracked image
            if (this.detector) {
                this.detector.updateImageState(image.id, {
                    state: 'playing',
                    animationData,
                });
            }

            // Start CSS animation playback immediately (for 'auto' and 'css' modes)
            if (imageMode !== 'video') {
                this.renderer!.play(image, animationData, {
                    intensity: image.config.intensity,
                    loop: image.config.loop,
                    onComplete: () => {
                        if (this.detector) {
                            this.detector.updateImageState(image.id, { state: 'idle' });
                        }
                        this.emitEvent('animationComplete', image);
                    },
                });

                this.emitEvent('animationStart', image);
            }

            // Start video generation in background (for 'auto' and 'video' modes)
            if (imageMode !== 'css' && this.veoClient && this.videoRenderer) {
                this.startVideoGeneration(image);
            }

        } catch (error) {
            // Handle error - graceful degradation to static image
            const livePhotoError = error instanceof LivePhotoError
                ? error
                : new LivePhotoError(
                    error instanceof Error ? error.message : 'Unknown error',
                    'UNKNOWN_ERROR',
                    error instanceof Error ? error : undefined
                );

            if (this.detector) {
                this.detector.updateImageState(image.id, {
                    state: 'error',
                    error: livePhotoError,
                });
            }

            if (this.options?.debug) {
                console.error('[beautifi] Animation error:', livePhotoError);
            }

            this.emitEvent('animationError', image, livePhotoError);
        }
    }

    /**
     * Start video generation in background
     */
    private async startVideoGeneration(image: TrackedImage): Promise<void> {
        if (!this.veoClient || !this.videoRenderer) return;

        if (image.config.videoUrl) {
            if (this.options?.debug) {
                console.log('[beautifi] Using portal-provided video for:', image.id);
            }
            this.transitionToVideo(image, image.config.videoUrl);
            return;
        }

        // Check if already generating or cached
        const cachedVideo = this.veoClient.getCachedVideo(image.imageUrl);
        if (cachedVideo) {
            if (this.options?.debug) {
                console.log('[beautifi] Using cached video for:', image.id);
            }
            this.transitionToVideo(image, cachedVideo);
            return;
        }

        // Emit video start event
        this.emitVideoEvent('videoStart', image);

        if (this.options?.debug) {
            console.log('[beautifi] Starting video generation for:', image.id);
        }

        try {
            let operationId = image.config.videoOperationId;

            if (!operationId) {
                // Start generation only when the portal has not already started one.
                const result = await this.veoClient.startGeneration({
                    imageUrl: image.imageUrl,
                });

                if (!result.success || !result.operationId) {
                    throw new Error(result.error?.message || 'Failed to start video generation');
                }

                // If already completed (cached on server side)
                if (result.status === 'completed' && result.videoUrl) {
                    this.transitionToVideo(image, result.videoUrl);
                    return;
                }

                operationId = result.operationId;
            }

            if (!operationId) {
                throw new Error('Missing video operation ID');
            }

            // Track pending operation
            this.pendingVideoGenerations.set(image.id, operationId);

            // Poll for completion
            const videoUrl = await this.veoClient.pollUntilComplete(
                operationId,
                image.imageUrl,
                (status, attempt) => {
                    if (this.options?.debug) {
                        console.log(`[beautifi] Video poll ${image.id}: ${status} (attempt ${attempt})`);
                    }
                }
            );

            // Clear pending
            this.pendingVideoGenerations.delete(image.id);

            if (videoUrl) {
                this.transitionToVideo(image, videoUrl);
            } else {
                if (this.options?.debug) {
                    console.log('[beautifi] Video generation failed, keeping CSS animation:', image.id);
                }
                this.emitVideoEvent('videoError', image);
            }

        } catch (error) {
            this.pendingVideoGenerations.delete(image.id);
            if (this.options?.debug) {
                console.error('[beautifi] Video generation error:', error);
            }
            this.emitVideoEvent('videoError', image);
            // CSS animation continues as fallback
        }
    }

    /**
     * Transition from CSS animation to video
     */
    private async transitionToVideo(image: TrackedImage, videoUrl: string): Promise<void> {
        if (!this.videoRenderer) return;

        if (this.options?.debug) {
            console.log('[beautifi] Transitioning to video:', image.id);
        }

        // Prepare video (load it behind the image)
        const prepared = await this.videoRenderer.prepareVideo(
            image.id,
            image.element,
            videoUrl,
            () => {
                // Fallback: keep CSS animation running
                if (this.options?.debug) {
                    console.log('[beautifi] Video fallback to CSS:', image.id);
                }
            }
        );

        if (prepared) {
            // Stop CSS animation and start video
            this.renderer?.stop(image.id);
            this.videoRenderer.play(image.id);
            this.emitVideoEvent('videoReady', image, videoUrl);
        }
    }

    /**
     * Pause animation for an image
     */
    private pauseAnimation(image: TrackedImage): void {
        if (this.renderer) {
            this.renderer.pause(image.id);
        }

        if (this.detector) {
            this.detector.updateImageState(image.id, { state: 'paused' });
        }

        if (this.options?.debug) {
            console.log('[beautifi] Pausing animation for:', image.imageUrl);
        }

        this.emitEvent('animationPause', image);
    }

    /**
     * Resume animation for an image
     */
    private resumeAnimation(image: TrackedImage): void {
        if (this.renderer) {
            this.renderer.resume(image.id);
        }

        if (this.detector) {
            this.detector.updateImageState(image.id, { state: 'playing' });
        }

        if (this.options?.debug) {
            console.log('[beautifi] Resuming animation for:', image.imageUrl);
        }

        this.emitEvent('animationResume', image);
    }

    /**
     * Emit lifecycle events
     */
    private emitEvent(
        type: 'animationStart' | 'animationComplete' | 'animationPause' | 'animationResume' | 'animationError',
        image: TrackedImage,
        error?: LivePhotoError
    ): void {
        if (typeof CustomEvent !== 'undefined') {
            const detail = {
                type,
                timestamp: Date.now(),
                element: image.element,
                imageId: image.id,
                ...(error && { error }),
            };
            const event = new CustomEvent(`beautifi:${type}`, { detail });
            document.dispatchEvent(event);
        }
    }

    /**
     * Emit video-related events
     */
    private emitVideoEvent(
        type: 'videoStart' | 'videoReady' | 'videoError',
        image: TrackedImage,
        videoUrl?: string
    ): void {
        if (typeof CustomEvent !== 'undefined') {
            const detail = {
                type,
                timestamp: Date.now(),
                element: image.element,
                imageId: image.id,
                ...(videoUrl && { videoUrl }),
            };
            const event = new CustomEvent(`beautifi:${type}`, { detail });
            document.dispatchEvent(event);
        }
    }
    /**
     * Check if the plugin has been initialized
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Get current options (for debugging)
     */
    getOptions(): Required<LivePhotoOptions> | null {
        return this.options;
    }

    /**
     * Get all tracked images
     */
    getTrackedImages(): TrackedImage[] {
        return this.detector?.getTrackedImages() ?? [];
    }

    /**
     * Destroy the plugin and clean up resources
     */
    destroy(): void {
        // Remove scroll handler
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
            this.scrollHandler = null;
        }

        // Destroy video loading queue
        if (this.videoQueue) {
            this.videoQueue.clear();
            this.videoQueue = null;
        }

        // Cancel any pending video operations
        if (this.veoClient) {
            this.veoClient.cancelAllOperations();
            this.veoClient = null;
        }

        // Destroy video renderer
        if (this.videoRenderer) {
            this.videoRenderer.destroy();
            this.videoRenderer = null;
        }

        if (this.renderer) {
            this.renderer.destroy();
            this.renderer = null;
        }

        if (this.detector) {
            this.detector.destroy();
            this.detector = null;
        }

        if (this.viewportObserver) {
            this.viewportObserver.destroy();
            this.viewportObserver = null;
        }

        if (this.preloadObserver) {
            this.preloadObserver.destroy();
            this.preloadObserver = null;
        }

        this.apiClient = null;
        this.settingsClient = null;
        this.pendingVideoGenerations.clear();
        this.trackedImagesMap.clear();
        this.initialized = false;
        this.options = null;
    }
}

// Create singleton instance
const plugin = new beautifiPlugin();

// Public API - Primary brand: beautifi
export const beautifi = {
    /**
     * Initialize the plugin
     */
    init: (options: LivePhotoOptions) => plugin.init(options),

    /**
     * Check if initialized
     */
    isInitialized: () => plugin.isInitialized(),

    /**
     * Get current options
     */
    getOptions: () => plugin.getOptions(),

    /**
     * Get tracked images
     */
    getTrackedImages: () => plugin.getTrackedImages(),

    /**
     * Destroy and clean up
     */
    destroy: () => plugin.destroy(),
};

// Backward compatibility alias
export const LiveMyPhotos = beautifi;

// Auto-init from script tag
if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        // Support both beautifi and legacy live-my-photos script names
        const scriptTag = document.querySelector<HTMLScriptElement>(
            'script[data-api-key][src*="beautifi"], script[data-api-key][src*="live-my-photos"]'
        );

        if (scriptTag) {
            const apiKey = scriptTag.dataset.apiKey;
            const autoInit = scriptTag.dataset.autoInit !== 'false';

            if (apiKey && autoInit) {
                beautifi.init({
                    apiKey,
                    selector: scriptTag.dataset.selector,
                    intensity: scriptTag.dataset.intensity as any,
                    type: scriptTag.dataset.type as any,
                    loop: scriptTag.dataset.loop !== 'false',
                    debug: scriptTag.dataset.debug === 'true',
                    mode: (scriptTag.dataset.mode as any) || 'auto',
                    optIn: scriptTag.dataset.optIn === 'true',
                });
            }
        }
    });
}

// UMD global export - Primary brand: beautifi
if (typeof window !== 'undefined') {
    (window as any).beautifi = beautifi;
    // Backward compatibility
    (window as any).LiveMyPhotos = beautifi;
}

// Export types and utilities
export type {
    LivePhotoOptions,
    TrackedImage,
} from './types';

export {
    LivePhotoError,
    DEFAULT_OPTIONS,
    DEFAULT_ANIMATION_CONFIG,
} from './types';

export { ImageDetector } from './ImageDetector';
export { ViewportObserver } from './ViewportObserver';
export { GeminiApiClient } from './GeminiApiClient';
export { AnimationRenderer } from './AnimationRenderer';
export { VeoClient } from './VeoClient';
export { VideoRenderer } from './VideoRenderer';
export { VideoLoadingQueue } from './VideoLoadingQueue';

export default beautifi;
